import { useState, useEffect, useMemo } from 'react';
import { MapPin, Building2, Users, GraduationCap, Home, Search, Eye, Phone, UserCheck } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useCrud } from '../../api/hooks/useCrud';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import './midias-municipal.css';

// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function schoolIcon(color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="width:28px;height:28px;border-radius:8px;background:${color};border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5"/></svg>
    </div>`,
    iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -28],
  });
}

function familyIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:6px;background:#7c3aed;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
    </div>`,
    iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -22],
  });
}

interface Escola {
  id: string; nome: string; codigo: string; tipoRede: string; endereco?: string;
  latitude?: number | null; longitude?: number | null; nomeDiretor?: string;
  telefone?: string; ativo: boolean;
  _count?: { turmas: number; alunos: number; professorEscolas?: number };
  municipio?: { nome: string; estado?: { sigla: string } };
}

interface Responsavel {
  id: string; nome: string; telefone?: string; fotoUrl?: string;
  alunos?: { aluno: { id: string; nome: string; latitude?: number | null; longitude?: number | null; turma?: { nome: string }; escola?: { id: string; nome: string; latitude?: number; longitude?: number } } }[];
}

type Layer = 'escolas' | 'familias' | 'todos';

const FALLBACK: [number, number] = [-19.92, -43.94]; // BH

export function MapaMunicipal() {
  const user = useAuthStore(s => s.user);
  const municipioId = user?.municipioId;
  const { data: escolas = [], isLoading } = useCrud<Escola>('escolas', { ...(municipioId ? { municipioId } : {}), ativo: 'true' });
  const [respPorEscola, setRespPorEscola] = useState<Record<string, { escola: Escola; resps: Responsavel[] }>>({});
  const [loadingResp, setLoadingResp] = useState(true);
  const [layer, setLayer] = useState<Layer>('todos');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Escola | null>(null);
  // Geocoded coords for schools without lat/lng
  const [geocoded, setGeocoded] = useState<Record<string, { lat: number; lng: number }>>({});
  const [geocoding, setGeocoding] = useState(false);

  // ── Geocode schools that have endereco but no coordinates ──
  useEffect(() => {
    if (!escolas.length) return;
    const semCoord = escolas.filter(e => e.ativo && !e.latitude && !e.longitude && e.endereco);
    if (!semCoord.length) return;

    setGeocoding(true);
    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));

    (async () => {
      const results: Record<string, { lat: number; lng: number }> = {};
      for (const e of semCoord) {
        try {
          // Nominatim free API (1 req/sec rate limit)
          const q = encodeURIComponent(`${e.endereco}, Brasil`);
          const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
            headers: { 'User-Agent': 'AlunoConectado/1.0' },
          });
          const data = await resp.json();
          if (data?.[0]) {
            results[e.id] = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
          }
          await delay(1100); // Rate limit: max 1 req/sec
        } catch { /* ignore */ }
      }
      setGeocoded(prev => ({ ...prev, ...results }));
      setGeocoding(false);
    })();
  }, [escolas]);

  // ── Merge: escolas with native coords + geocoded ones ──
  const escolasAtivas = useMemo(() => escolas.filter(e => e.ativo), [escolas]);
  const escolasComCoord = useMemo(() => {
    return escolasAtivas.map(e => {
      if (e.latitude && e.longitude) return e;
      const gc = geocoded[e.id];
      if (gc) return { ...e, latitude: gc.lat, longitude: gc.lng };
      return e;
    }).filter(e => e.latitude && e.longitude);
  }, [escolasAtivas, geocoded]);

  // Load responsáveis per escola (endpoint is scoped: escolas/:id/responsaveis)
  useEffect(() => {
    if (!escolasComCoord.length) { setLoadingResp(false); return; }

    Promise.all(
      escolasComCoord.map(e =>
        api.get(ROUTES.responsaveis.listar(e.id))
          .then(r => {
            const resps: Responsavel[] = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
            return { escola: e, resps };
          })
          .catch(() => ({ escola: e, resps: [] as Responsavel[] }))
      )
    ).then(results => {
      const map: Record<string, { escola: Escola; resps: Responsavel[] }> = {};
      results.forEach(r => {
        if (r.resps.length > 0) map[r.escola.id] = r;
      });
      setRespPorEscola(map);
    }).finally(() => setLoadingResp(false));
  }, [escolasComCoord.length]);

  const totalFamilias = Object.values(respPorEscola).reduce((s, g) => s + g.resps.length, 0);

  // Center map on first school or fallback
  const center: [number, number] = useMemo(() => {
    if (escolasComCoord.length) {
      const avgLat = escolasComCoord.reduce((s, e) => s + e.latitude!, 0) / escolasComCoord.length;
      const avgLng = escolasComCoord.reduce((s, e) => s + e.longitude!, 0) / escolasComCoord.length;
      return [avgLat, avgLng];
    }
    return FALLBACK;
  }, [escolasComCoord]);

  // KPIs
  const totalAlunos = escolasAtivas.reduce((s, e) => s + (e._count?.alunos || 0), 0);
  const totalTurmas = escolasAtivas.reduce((s, e) => s + (e._count?.turmas || 0), 0);

  // Filter
  const q = search.trim().toLowerCase();
  const filtered = escolasAtivas.filter(e => !q || [e.nome, e.codigo, e.nomeDiretor].join(' ').toLowerCase().includes(q));

  if (isLoading) return (
    <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid var(--border-color)', borderTop: '4px solid #1e40af', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted)' }}>Carregando mapa...</p>
      </div>
    </div>
  );

  return (
    <div className="admin-page animate-fadeIn" style={{ marginTop: -22 }}>
      {/* ═══ HERO STRIP — Padrão Banco de Mídias ═══ */}
      <div className="mdm-hero" style={{ borderRadius: '12px 12px 0 0', marginBottom: 0 }}>
        <div className="mdm-hero-circle c1" />
        <div className="mdm-hero-circle c2" />
        <div className="mdm-hero-content">
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="mdm-hero-badge">
              <MapPin size={10} /> COBERTURA MUNICIPAL
            </div>
            <h1 className="mdm-hero-title">Mapa Estratégico</h1>
            <p className="mdm-hero-subtitle">
              Geolocalização, densidade territorial e distribuição de escolas e famílias na rede.
            </p>
          </div>
          <div className="mdm-hero-stats">
            {[
              { icon: Building2, val: escolasAtivas.length, lbl: 'Escolas', clr: '#93c5fd' },
              { icon: GraduationCap, val: totalAlunos.toLocaleString('pt-BR'), lbl: 'Alunos', clr: '#a5f3fc' },
              { icon: Users, val: totalFamilias, lbl: 'Famílias', clr: '#c4b5fd' },
              { icon: Home, val: totalTurmas, lbl: 'Turmas', clr: '#fbbf24' },
            ].map((s, i) => (
              <div key={i} className="mdm-hero-stat">
                <div className="mdm-hero-stat-icon" style={{ background: `${s.clr}20`, color: s.clr }}>
                  <s.icon size={12} />
                </div>
                <div>
                  <div className="mdm-hero-stat-value">{s.val}</div>
                  <div className="mdm-hero-stat-label">{s.lbl}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ background: 'var(--bg-card, white)', borderRadius: '0 0 16px 16px', border: '1px solid var(--border-color, #e5e7eb)', borderTop: 'none', padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Camada:</span>
        {([
          { key: 'todos' as Layer, label: 'Todos', color: '#1e40af' },
          { key: 'escolas' as Layer, label: 'Escolas', color: '#10b981' },
          { key: 'familias' as Layer, label: 'Familias', color: '#7c3aed' },
        ]).map(m => {
          const active = layer === m.key;
          return (
            <button key={m.key} onClick={() => setLayer(m.key)} style={{
              padding: '3px 10px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: active ? m.color : 'var(--bg-main, #f1f5f9)',
              color: active ? 'white' : 'var(--text-muted)',
              border: active ? 'none' : '1px solid var(--border-color, #e5e7eb)',
              cursor: 'pointer', transition: 'all .15s',
            }}>{m.label}</button>
          );
        })}
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative', minWidth: 180 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" placeholder="Buscar escola..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '6px 10px 6px 30px', borderRadius: 8, fontSize: '.78rem', border: '1px solid var(--border-color)', background: 'var(--bg-main)', color: 'var(--text-main)' }} />
        </div>
      </div>

      {/* MAP */}
      <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)', boxShadow: '0 4px 24px rgba(0,0,0,.08)', height: 540, position: 'relative' }}>
        <MapContainer center={center} zoom={12} minZoom={4} style={{ height: '100%', width: '100%', zIndex: 1 }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
          />

          {/* Escola markers */}
          {(layer === 'escolas' || layer === 'todos') && escolasComCoord.filter(e => {
            if (!search.trim()) return true;
            const q2 = search.trim().toLowerCase();
            return [e.nome, e.codigo, e.nomeDiretor].join(' ').toLowerCase().includes(q2);
          }).map(e => {
            const isGeocoded = !!geocoded[e.id];
            return (
            <Marker key={e.id} position={[e.latitude!, e.longitude!]} icon={schoolIcon(isGeocoded ? '#059669' : '#1e40af')}>
              <Popup>
                <div style={{ minWidth: 220, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: '#111', marginBottom: 2 }}>{e.nome}</div>
                  <div style={{ fontSize: '.75rem', color: '#6b7280', marginBottom: 8 }}>{e.tipoRede} · {e.codigo}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    {[
                      { label: 'Alunos', val: e._count?.alunos || 0, c: '#1e40af' },
                      { label: 'Turmas', val: e._count?.turmas || 0, c: '#7c3aed' },
                    ].map(d => (
                      <div key={d.label} style={{ background: `${d.c}08`, borderRadius: 8, padding: '5px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 600 }}>{d.label}</div>
                        <div style={{ fontWeight: 800, fontSize: '.85rem', color: d.c }}>{d.val}</div>
                      </div>
                    ))}
                  </div>
                  {e.nomeDiretor && <div style={{ fontSize: '.75rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><UserCheck size={12} /> {e.nomeDiretor}</div>}
                  {e.telefone && <div style={{ fontSize: '.75rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><Phone size={12} /> {e.telefone}</div>}
                  {e.endereco && <div style={{ fontSize: '.72rem', color: '#9ca3af', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={11} /> {e.endereco}</div>}
                </div>
              </Popup>
            </Marker>
            );
          })}

          {/* Família markers — coordenadas reais dos alunos ou fallback determinístico */}
          {(layer === 'familias' || layer === 'todos') && Object.values(respPorEscola).flatMap(g => {
            return g.resps.map((r, i) => {
              // Tentar usar coordenadas reais de algum aluno vinculado
              const alunoComGeo = r.alunos?.find(a => a.aluno?.latitude && a.aluno?.longitude);
              let lat: number;
              let lng: number;

              if (alunoComGeo?.aluno?.latitude && alunoComGeo?.aluno?.longitude) {
                // Coordenadas reais do CEP do aluno
                lat = alunoComGeo.aluno.latitude;
                lng = alunoComGeo.aluno.longitude;
              } else {
                // Fallback: offset determinístico a partir da escola
                const seed1 = r.id.charCodeAt(0) + r.id.charCodeAt(r.id.length - 1) + i * 7;
                const seed2 = r.id.charCodeAt(1) + r.id.charCodeAt(Math.floor(r.id.length / 2)) + i * 13;
                const angle = (seed1 * 137.508) % 360;
                const dist = 0.003 + ((seed2 * 31) % 120) / 10000;
                lat = g.escola.latitude! + dist * Math.sin(angle * Math.PI / 180);
                lng = g.escola.longitude! + dist * Math.cos(angle * Math.PI / 180);
              }

              return (
                <Marker key={`fam-${r.id}`} position={[lat, lng]} icon={familyIcon()}>
                  <Popup>
                    <div style={{ minWidth: 180, fontFamily: 'Inter, system-ui, sans-serif' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        {r.fotoUrl ? (
                          <img src={r.fotoUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e9d5ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.55rem', fontWeight: 800 }}>
                            {r.nome.split(' ').map(n => n[0]).slice(0, 2).join('')}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '.82rem', color: '#111' }}>{r.nome}</div>
                          {r.telefone && <div style={{ fontSize: '.65rem', color: '#9ca3af' }}>{r.telefone}</div>}
                        </div>
                      </div>
                      <div style={{ fontSize: '.68rem', color: '#6b7280', background: '#f5f3ff', borderRadius: 6, padding: '4px 8px' }}>
                        <Building2 size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                        {g.escola.nome}
                      </div>
                      {r.alunos && r.alunos.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: '.65rem', color: '#6b7280' }}>
                          {r.alunos.map(a => a.aluno?.nome).filter(Boolean).join(', ')}
                        </div>
                      )}
                      {alunoComGeo && (
                        <div style={{ marginTop: 3, fontSize: '.58rem', color: '#16a34a', fontWeight: 500 }}>
                          ✓ Localização real via CEP
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            });
          })}
        </MapContainer>

        {/* Legend */}
        <div style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 1000, background: 'white', borderRadius: 12, padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,.12)', fontSize: '.72rem', minWidth: 140 }}>
          <div style={{ fontWeight: 800, fontSize: '.75rem', color: '#111', marginBottom: 6 }}>Legenda</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1e40af' }} /> Escola (cadastrada)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669' }} /> Escola (geocodificada)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#7c3aed' }} /> Familias</div>
        </div>

        {/* Counter */}
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, background: 'rgba(255,255,255,.95)', borderRadius: 8, padding: '6px 12px', fontSize: '.72rem', fontWeight: 600, color: '#374151', boxShadow: '0 2px 8px rgba(0,0,0,.1)', backdropFilter: 'blur(8px)' }}>
          <Eye size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {escolasComCoord.length} escolas · {totalFamilias} familias
          {geocoding && <span style={{ color: '#d97706', marginLeft: 6 }}>· Geocodificando...</span>}
          {Object.keys(geocoded).length > 0 && !geocoding && (
            <span style={{ color: '#059669', marginLeft: 6 }}>· {Object.keys(geocoded).length} via endereco</span>
          )}
        </div>
      </div>
    </div>
  );
}
